/**
 * Mirror Configurator Modal
 */

(function() {
  'use strict';

  const state = {
    currentStep: 1,
    projectType: 'residential',
    serviceType: 'delivery-installation',
    walls: [
      {
        id: 1,
        size: '36x60',
        finish: 'satin-silver',
        orientation: 'vertical',
        quantity: 1
      }
    ],
    addons: {
      installationFinish: null, // 'standard' | 'premium' | null until user selects
      adhesive: false,
      cutouts: false,
      cutoutsCount: 1,
      outletCoverType: 'light-switch',
      wallSurface: null // set when user selects wall surface
    },
    installerPhotos: [],
    totalPrice: 0
  };

  function initModal() {
    const modal = document.querySelector('[data-mirror-modal]');
    if (!modal) return;

    const overlay = modal.querySelector('[data-modal-overlay]');
    const closeBtn = modal.querySelector('[data-modal-close]');
    const triggers = document.querySelectorAll('[data-cta-button], [data-open-modal]');

    triggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        openModal();
      });
    });

    if (overlay) {
      overlay.addEventListener('click', closeModal);
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', closeModal);
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && modal.classList.contains('is-active')) {
        closeModal();
      }
    });
  }

  function openModal() {
    const modal = document.querySelector('[data-mirror-modal]');
    if (!modal) return;

    modal.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    // Reset summary sections visibility for progressive disclosure
    const summarySections = document.querySelectorAll('[data-summary-section]');
    summarySections.forEach(section => {
      section.classList.remove('mirror-modal__summary-section--visible');
    });

    // Ensure state is reset to step 1 and update visibility
    state.currentStep = 1;
    updateStep();
  }

  function closeModal() {
    const modal = document.querySelector('[data-mirror-modal]');
    if (!modal) return;

    modal.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  function initAccordion() {
    const accordionToggle = document.querySelector('[data-accordion-toggle]');
    const sidebar = document.querySelector('.mirror-modal__sidebar');
    if (!accordionToggle || !sidebar) return;

    accordionToggle.addEventListener('click', () => {
      const isExpanded = accordionToggle.getAttribute('aria-expanded') === 'true';
      const newState = !isExpanded;
      accordionToggle.setAttribute('aria-expanded', newState);
      
      // Toggle class on sidebar for CSS targeting
      if (newState) {
        sidebar.classList.add('is-expanded');
      } else {
        sidebar.classList.remove('is-expanded');
      }
    });

    // On desktop (>= 1000px), force accordion to be always expanded
    const checkViewport = () => {
      if (window.innerWidth >= 1000) {
        accordionToggle.setAttribute('aria-expanded', 'true');
        sidebar.classList.add('is-expanded');
      }
    };

    checkViewport();
    window.addEventListener('resize', checkViewport);
  }

  function initOptions() {
    const options = document.querySelectorAll('.mirror-modal__option');

    options.forEach(option => {
      const radio = option.querySelector('.mirror-modal__radio');
      if (!radio) return;

      option.addEventListener('click', (e) => {
        e.preventDefault();
        
        const fieldType = radio.getAttribute('data-field');
        const fieldName = radio.getAttribute('name');
        const value = radio.value;

        const allOptionsInGroup = document.querySelectorAll(`input[name="${fieldName}"]`);
        allOptionsInGroup.forEach(r => {
          r.closest('.mirror-modal__option').classList.remove('mirror-modal__option--selected');
        });

        radio.checked = true;
        option.classList.add('mirror-modal__option--selected');

        if (fieldType === 'project-type') {
          state.projectType = value;
        } else if (fieldType === 'service-type') {
          state.serviceType = value;
          updateConditionalFields();
        }

        updateSummary();
        calculatePrice();
      });
    });
  }

  function initButtonOptions() {
    const buttonOptions = document.querySelectorAll('.mirror-modal__button-option, .mirror-modal__orientation-btn');

    buttonOptions.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        
        const field = button.getAttribute('data-field');
        const value = button.getAttribute('data-value');
        
        // Get wall index from parent section
        const wallSection = button.closest('[data-wall-section]');
        const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;

        // Remove active from siblings
        const siblings = button.parentElement.querySelectorAll('.mirror-modal__button-option, .mirror-modal__orientation-btn');
        siblings.forEach(btn => btn.classList.remove('mirror-modal__button-option--active', 'mirror-modal__orientation-btn--active'));

        // Add active to clicked
        button.classList.add(button.classList.contains('mirror-modal__orientation-btn') ? 'mirror-modal__orientation-btn--active' : 'mirror-modal__button-option--active');

        // Update state for this wall
        if (state.walls[wallIndex]) {
          if (field === 'size') {
            state.walls[wallIndex].size = value;
          }
          if (field === 'finish') {
            state.walls[wallIndex].finish = value;
          }
          if (field === 'orientation') {
            state.walls[wallIndex].orientation = value;
          }
        }

        updateSummary();
        calculatePrice();
      });
    });
  }

  function initCardOptions() {
    // Handle size card selections
    const sizeCards = document.querySelectorAll('.mirror-modal__size-card');
    sizeCards.forEach(card => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        
        const radio = card.querySelector('.mirror-modal__size-radio');
        if (!radio) return;

        const field = radio.getAttribute('data-field');
        const value = radio.value;
        
        // Get wall index from parent section
        const wallSection = card.closest('[data-wall-section]');
        const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;

        // Remove selected from siblings
        const siblings = card.parentElement.querySelectorAll('.mirror-modal__size-card');
        siblings.forEach(c => c.classList.remove('mirror-modal__size-card--selected'));

        // Add selected to clicked
        card.classList.add('mirror-modal__size-card--selected');
        radio.checked = true;

        // Update state
        if (state.walls[wallIndex] && field === 'size') {
          state.walls[wallIndex].size = value;
        }

        updateSummary();
        calculatePrice();
      });
    });

    // Handle finish card selections
    const finishCards = document.querySelectorAll('.mirror-modal__finish-card');
    finishCards.forEach(card => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        
        const radio = card.querySelector('.mirror-modal__finish-radio');
        if (!radio) return;

        const field = radio.getAttribute('data-field');
        const value = radio.value;
        
        // Get wall index from parent section
        const wallSection = card.closest('[data-wall-section]');
        const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;

        // Remove selected from siblings
        const siblings = card.parentElement.querySelectorAll('.mirror-modal__finish-card');
        siblings.forEach(c => c.classList.remove('mirror-modal__finish-card--selected'));

        // Add selected to clicked
        card.classList.add('mirror-modal__finish-card--selected');
        radio.checked = true;

        // Update state
        if (state.walls[wallIndex] && field === 'finish') {
          state.walls[wallIndex].finish = value;
        }

        updateSummary();
        calculatePrice();
      });
    });
  }

  function initQuantitySelectors() {
    const qtyButtons = document.querySelectorAll('.mirror-modal__qty-btn');

    qtyButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        
        const action = btn.getAttribute('data-qty-action');
        const target = btn.getAttribute('data-qty-target') || 'quantity';
        const input = btn.parentElement.querySelector('.mirror-modal__qty-input');
        
        if (!input) return;

        let value = parseInt(input.value) || 0;
        const min = parseInt(input.getAttribute('min')) || 0;
        const max = parseInt(input.getAttribute('max')) || 999;

        if (action === 'increase' && value < max) {
          value++;
        } else if (action === 'decrease' && value > min) {
          value--;
        }

        input.value = value;

        // Update state
        if (target === 'quantity') {
          const wallSection = btn.closest('[data-wall-section]');
          const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;
          if (state.walls[wallIndex]) {
            state.walls[wallIndex].quantity = value;
          }
        } else if (target === 'cutouts') {
          state.addons.cutoutsCount = value;
        }

        updateSummary();
        calculatePrice();
      });
    });

    // Also handle direct input
    const qtyInputs = document.querySelectorAll('.mirror-modal__qty-input');
    qtyInputs.forEach(input => {
      input.addEventListener('change', () => {
        const field = input.getAttribute('data-field');
        const value = parseInt(input.value) || 0;

        if (field === 'quantity') {
          const wallSection = input.closest('[data-wall-section]');
          const wallIndex = wallSection ? parseInt(wallSection.getAttribute('data-wall-section')) : 0;
          if (state.walls[wallIndex]) {
            state.walls[wallIndex].quantity = value;
          }
        } else if (field === 'cutouts') {
          state.addons.cutoutsCount = value;
        }

        updateSummary();
        calculatePrice();
      });
    });
  }

  function initCheckboxes() {
    // Optional add-ons: independent checkboxes (none / adhesive / cutouts / both)
    const addonInputs = document.querySelectorAll('[data-addon]');

    addonInputs.forEach(input => {
      input.addEventListener('change', () => {
        const addon = input.getAttribute('data-addon');
        if (addon === 'adhesive') {
          state.addons.adhesive = input.checked;
        } else if (addon === 'cutouts') {
          state.addons.cutouts = input.checked;
          toggleCutoutDetails(input.checked);
        }

        updateSummary();
        calculatePrice();
      });
    });

    // Handle installation finish radio buttons
    const installFinishRadios = document.querySelectorAll('[data-field="installation-finish"]');
    
    installFinishRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        e.preventDefault();
        state.addons.installationFinish = radio.value;
        updateSummary();
        calculatePrice();
      });

      // Prevent scroll on label click
      const label = radio.closest('.mirror-modal__addon-card');
      if (label) {
        label.addEventListener('click', (e) => {
          e.preventDefault();
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });

    // Handle wall surface radio buttons
    const wallSurfaceRadios = document.querySelectorAll('[data-field="wall-surface"]');
    
    wallSurfaceRadios.forEach(radio => {
      radio.addEventListener('change', (e) => {
        e.preventDefault();
        state.addons.wallSurface = radio.value;
        updateSummary();
        calculatePrice();
      });

      // Prevent scroll on label click
      const label = radio.closest('.mirror-modal__radio-option');
      if (label) {
        label.addEventListener('click', (e) => {
          e.preventDefault();
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }
    });
  }

  function toggleCutoutDetails(show) {
    const cutoutDetails = document.querySelector('[data-conditional="cutouts-only"]');
    if (cutoutDetails) {
      cutoutDetails.style.display = show ? 'block' : 'none';
    }
  }

  function initSelects() {
    // Cutouts count select
    const cutoutsCountSelect = document.querySelector('[data-field="cutouts-count"]');
    
    if (cutoutsCountSelect) {
      cutoutsCountSelect.addEventListener('change', (e) => {
        state.addons.cutoutsCount = parseInt(e.target.value, 10) || 1;
        updateSummary();
        calculatePrice();
      });
    }

    // Outlet cover type select
    const outletCoverSelect = document.querySelector('[data-field="outlet-cover-type"]');
    
    if (outletCoverSelect) {
      outletCoverSelect.addEventListener('change', (e) => {
        state.addons.outletCoverType = e.target.value;
        updateSummary();
        calculatePrice();
      });
    }

    // Wall surface select
    const wallSurfaceSelect = document.querySelector('[data-field="wall-surface"]');
    
    if (wallSurfaceSelect) {
      wallSurfaceSelect.addEventListener('change', (e) => {
        state.addons.wallSurface = e.target.value;
        updateSummary();
        calculatePrice();
      });
    }
  }

  function initPhotoUpload() {
    const photoAddBtn = document.querySelector('[data-photo-add-btn]');
    const photoInput = document.querySelector('[data-photo-input]');
    const photoUploadArea = document.querySelector('[data-photo-upload-area]');

    if (photoAddBtn && photoInput) {
      photoAddBtn.addEventListener('click', (e) => {
        e.preventDefault();
        photoInput.click();
      });

      photoInput.addEventListener('change', (e) => {
        const files = Array.from(e.target.files);
        files.forEach(file => {
          if (file.type.startsWith('image/')) {
            addPhotoPreview(file, photoUploadArea, photoAddBtn);
          }
        });
        photoInput.value = ''; // Reset input
      });
    }
  }

  function addPhotoPreview(file, container, addBtn) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const preview = document.createElement('div');
      preview.className = 'mirror-modal__photo-preview';
      
      const img = document.createElement('img');
      img.className = 'mirror-modal__photo-preview-image';
      img.src = e.target.result;
      
      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'mirror-modal__photo-remove-btn';
      removeBtn.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M6 6L18 18M6 18L18 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `;
      
      removeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        preview.remove();
        const index = state.installerPhotos.indexOf(file);
        if (index > -1) {
          state.installerPhotos.splice(index, 1);
        }
      });
      
      preview.appendChild(img);
      preview.appendChild(removeBtn);
      
      // Insert before the add button
      container.insertBefore(preview, addBtn);
      
      // Add to state
      state.installerPhotos.push(file);
    };
    reader.readAsDataURL(file);
  }

  function initNavigation() {
    const prevBtn = document.querySelector('[data-nav="prev"]');
    const nextBtn = document.querySelector('[data-nav="next"]');
    const submitBtn = document.querySelector('[data-submit-btn]');
    const submitBtnCollapsed = document.querySelector('[data-submit-btn-collapsed]');

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        if (state.currentStep > 1) {
          state.currentStep--;
          updateStep();
        }
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        if (state.currentStep < 3) {
          state.currentStep++;
          updateStep();
        } else if (window.innerWidth >= 1000) {
          handleAddToCart();
        }
      });
    }

    if (submitBtn) {
      submitBtn.addEventListener('click', handleAddToCart);
    }

    if (submitBtnCollapsed) {
      submitBtnCollapsed.addEventListener('click', handleAddToCart);
    }
  }

  async function handleAddToCart() {
    const submitBtn = document.querySelector('[data-submit-btn]');
    const submitBtnCollapsed = document.querySelector('[data-submit-btn-collapsed]');
    const nextBtn = document.querySelector('[data-nav="next"]');
    const isDesktopLastStep = window.innerWidth >= 1000 && state.currentStep === 3;

    if (!submitBtn && !submitBtnCollapsed) return;

    const originalText = submitBtn ? submitBtn.textContent : '';
    const originalTextCollapsed = submitBtnCollapsed ? submitBtnCollapsed.textContent : '';

    function restoreSubmitState() {
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = originalText;
      }
      if (submitBtnCollapsed) {
        submitBtnCollapsed.disabled = false;
        submitBtnCollapsed.textContent = originalTextCollapsed;
      }
      if (isDesktopLastStep && nextBtn) {
        nextBtn.disabled = false;
        nextBtn.classList.remove('mirror-modal__btn--loading');
      }
    }

    // Disable all buttons and show loading state
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Adding to Cart...';
    }
    if (submitBtnCollapsed) {
      submitBtnCollapsed.disabled = true;
      submitBtnCollapsed.textContent = 'Adding to Cart...';
    }
    if (isDesktopLastStep && nextBtn) {
      nextBtn.disabled = true;
      nextBtn.classList.add('mirror-modal__btn--loading');
    }

    try {
      const productData = window.mirrorConfigData || {};
      const mirrorProduct = productData.mirror;
      const addons = productData.addons || {};

      if (!mirrorProduct) {
        throw new Error('Mirror product data not available');
      }

      if (state.serviceType === 'delivery-installation') {
        if (!state.addons.installationFinish) {
          alert('Please select an installation finish (Standard or Premium).');
          restoreSubmitState();
          return;
        }
        if (!state.addons.wallSurface) {
          alert('Please select a wall surface.');
          restoreSubmitState();
          return;
        }
      }

      // Build items array for JSON submission
      const items = [];

      // Resize uploaded photos to tiny thumbnails so they fit in Shopify cart properties
      function resizeToThumb(imgSrc, maxPx, quality) {
        return new Promise(function(resolve) {
          var img = new Image();
          img.onload = function() {
            var w = img.width, h = img.height;
            if (w > h) { if (w > maxPx) { h = h * maxPx / w; w = maxPx; } }
            else { if (h > maxPx) { w = w * maxPx / h; h = maxPx; } }
            var c = document.createElement('canvas');
            c.width = w; c.height = h;
            c.getContext('2d').drawImage(img, 0, 0, w, h);
            resolve(c.toDataURL('image/jpeg', quality));
          };
          img.onerror = function() { resolve(''); };
          img.src = imgSrc;
        });
      }

      const photoThumbs = [];
      const previewImgs = document.querySelectorAll('.mirror-modal__photo-preview-image');
      for (const pImg of previewImgs) {
        if (pImg.src) {
          const thumb = await resizeToThumb(pImg.src, 120, 0.4);
          if (thumb) photoThumbs.push(thumb);
        }
      }

      // Build shared display properties for add-ons (visible in cart)
      const serviceLabel = state.serviceType === 'delivery-installation'
        ? 'Delivery + Installation' : 'Delivery Only (DIY)';

      const sharedAddonProps = {};
      sharedAddonProps['Service'] = serviceLabel;

      if (state.serviceType === 'delivery-installation') {
        sharedAddonProps['Installation finish'] = state.addons.installationFinish === 'premium' ? 'Premium' : 'Standard';
      }
      if (state.addons.adhesive) {
        sharedAddonProps['Adhesive'] = 'Yes';
      }
      if (state.addons.cutouts && state.addons.cutoutsCount > 0) {
        sharedAddonProps['Cutouts'] = String(state.addons.cutoutsCount);
      }
      if (state.serviceType === 'delivery-installation' && state.addons.wallSurface) {
        sharedAddonProps['Wall surface'] = getWallSurfaceLabel(state.addons.wallSurface);
      }

      // Add mirror products for each wall
      for (const [index, wall] of state.walls.entries()) {
        const variant = getMirrorVariant(mirrorProduct, wall.size, wall.finish);
        
        if (variant) {
          const props = {
            _config_id: `WALL-${index + 1}`,
            _wall_label: `Wall ${index + 1}`,
            _project_type: state.projectType,
            _service_type: state.serviceType,
            _mirror_size: getSizeLabel(wall.size),
            _channel_finish: getFinishLabel(wall.finish),
            _orientation: getOrientationLabel(wall.orientation),
            _quantity_for_wall: wall.quantity,
            'Size': getSizeLabel(wall.size),
            'Mirror Orientation': getOrientationLabel(wall.orientation),
            'Channel finish': getFinishLabel(wall.finish),
            ...sharedAddonProps
          };

          if (index === 0 && photoThumbs.length > 0) {
            photoThumbs.forEach((thumb, i) => {
              props[`_photo_${i + 1}`] = thumb;
            });
          }

          const mirrorItem = {
            id: variant.id,
            quantity: wall.quantity,
            properties: props
          };
          items.push(mirrorItem);
        } else {
          throw new Error(`Could not find variant for ${getSizeLabel(wall.size)} / ${getFinishLabel(wall.finish)}`);
        }
      }

      // Calculate total mirrors
      const totalMirrors = state.walls.reduce((sum, wall) => sum + wall.quantity, 0);

      // Add installation (if selected)
      if (state.serviceType === 'delivery-installation' && addons.installation) {
        const installationVariant = addons.installation.variants?.[0];
        if (installationVariant && installationVariant.id) {
          const installationItem = {
            id: installationVariant.id,
            quantity: totalMirrors,
            properties: {
              _addon_type: 'installation',
              _applies_to_mirrors: totalMirrors,
              _project_type: state.projectType
            }
          };
          items.push(installationItem);
        }
      }

      // Add premium finish (if selected)
      if (state.serviceType === 'delivery-installation' && state.addons.installationFinish === 'premium' && addons.premiumFinish) {
        const premiumVariant = addons.premiumFinish.variants?.[0];
        if (premiumVariant && premiumVariant.id) {
          const premiumItem = {
            id: premiumVariant.id,
            quantity: totalMirrors,
            properties: {
              _addon_type: 'premium_finish',
              _applies_to_mirrors: totalMirrors
            }
          };
          items.push(premiumItem);
        }
      }

      // Add adhesive (if selected)
      if (state.addons.adhesive && addons.adhesive) {
        const adhesiveVariant = addons.adhesive.variants?.[0];
        if (adhesiveVariant && adhesiveVariant.id) {
          const adhesiveItem = {
            id: adhesiveVariant.id,
            quantity: 1,
            properties: {
              _addon_type: 'adhesive'
            }
          };
          items.push(adhesiveItem);
        }
      }

      // Add cutouts (if selected)
      if (state.addons.cutouts && state.addons.cutoutsCount > 0 && addons.cutout) {
        const cutoutVariant = addons.cutout.variants?.[0];
        if (cutoutVariant && cutoutVariant.id) {
          const cutoutItem = {
            id: cutoutVariant.id,
            quantity: state.addons.cutoutsCount,
            properties: {
              _addon_type: 'cutout',
              _cutouts_count: state.addons.cutoutsCount,
              _outlet_cover_type: state.addons.outletCoverType
            }
          };
          items.push(cutoutItem);
        }
      }

      // Add wall surface surcharge (if needed)
      if (state.serviceType === 'delivery-installation' && needsWallSurcharge(state.addons.wallSurface) && addons.wallSurcharge) {
        const surchargeVariant = addons.wallSurcharge.variants?.[0];
        if (surchargeVariant && surchargeVariant.id) {
          const surchargeItem = {
            id: surchargeVariant.id,
            quantity: totalMirrors,
            properties: {
              _addon_type: 'wall_surcharge',
              _wall_surface: state.addons.wallSurface,
              _applies_to_mirrors: totalMirrors
            }
          };
          items.push(surchargeItem);
        }
      }

      // Submit via fetch with JSON
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server error: ${response.status} - ${errorText}`);
      }

      const responseData = await response.json();

      // Success - redirect to cart
      window.location.href = '/cart';

    } catch (error) {
      alert('Could not add to cart. Please try again.\n\nError: ' + error.message);
      restoreSubmitState();
    }
  }

  function normalizeOptionString(str) {
    let s = str || '';
    try {
      if (typeof s.normalize === 'function') {
        s = s.normalize('NFKD');
      }
    } catch (e) {
      /* ignore */
    }
    return s
      .replace(/[\u2018\u2019\u2032\u2035\uFF07]/g, "'")
      .replace(/[\u201c\u201d]/g, '"')
      .toLowerCase();
  }

  function getMirrorVariantsSource(mirrorProduct) {
    const data = window.mirrorConfigData || {};
    if (Array.isArray(data.mirrorVariantCatalog) && data.mirrorVariantCatalog.length > 0) {
      return data.mirrorVariantCatalog;
    }
    if (mirrorProduct && Array.isArray(mirrorProduct.variants) && mirrorProduct.variants.length > 0) {
      return mirrorProduct.variants;
    }
    return [];
  }

  function getVariantSearchString(v) {
    return normalizeOptionString([
      v.title || '',
      v.option1 || '',
      v.option2 || '',
      v.option3 || ''
    ].join(' '));
  }

  function variantMatchesConfiguratorSize(optStr, size) {
    if (size === '36x60') {
      if (/\b36\s*[x×]\s*60\b/.test(optStr)) return true;
      if (/3\s*[''′]?\s*[x×]\s*5\s*[''′]?/.test(optStr)) return true;
      if (/\b3\s*[x×]\s*5\b/.test(optStr)) return true;
      if (optStr.includes('36x60')) return true;
      if (optStr.includes('36') && optStr.includes('60')) return true;
      return optStr.includes('36') && /(^|[^0-9])60([^0-9]|$)/.test(optStr);
    }
    if (size === '48x72') {
      if (/\b48\s*[x×]\s*72\b/.test(optStr)) return true;
      if (/4\s*[''′]?\s*[x×]\s*6\s*[''′]?/.test(optStr)) return true;
      if (/\b4\s*[x×]\s*6\b/.test(optStr)) return true;
      if (optStr.includes('48x72')) return true;
      if (optStr.includes('48') && optStr.includes('72')) return true;
      return optStr.includes('48') && /(^|[^0-9])72([^0-9]|$)/.test(optStr);
    }
    return false;
  }

  function variantMatchesConfiguratorFinish(optStr, finish) {
    if (finish === 'satin-silver') {
      if (optStr.includes('matte')) return false;
      return optStr.includes('satin') || optStr.includes('silver');
    }
    if (finish === 'matte-black') {
      return optStr.includes('matte') || /\bblack\b/.test(optStr);
    }
    return false;
  }

  function findMirrorVariant(mirrorProduct, size, finish) {
    const variants = getMirrorVariantsSource(mirrorProduct);
    if (!variants.length) {
      return null;
    }

    let match = variants.find(v => {
      const s = getVariantSearchString(v);
      return variantMatchesConfiguratorSize(s, size) && variantMatchesConfiguratorFinish(s, finish);
    });
    if (match) {
      return match;
    }

    const normalizedSize = size === '36x60' ? '36' : '48';
    const normalizedFinish = finish === 'satin-silver' ? 'satin' : 'black';

    match = variants.find(v => {
      const allOptions = [
        (v.option1 || '').toLowerCase(),
        (v.option2 || '').toLowerCase(),
        (v.option3 || '').toLowerCase()
      ].join(' ');

      return allOptions.includes(normalizedSize) && allOptions.includes(normalizedFinish);
    });
    if (match) {
      return match;
    }

    const sizeMatches = variants.filter(v => {
      const s = getVariantSearchString(v);
      return variantMatchesConfiguratorSize(s, size);
    });

    if (sizeMatches.length) {
      match = sizeMatches.find(v => {
        const s = getVariantSearchString(v);
        return variantMatchesConfiguratorFinish(s, finish);
      });
      if (match) {
        return match;
      }
      return sizeMatches[0];
    }

    return null;
  }

  function getMirrorVariant(mirrorProduct, size, finish) {
    return findMirrorVariant(mirrorProduct, size, finish);
  }

  function updateStep() {
    const steps = document.querySelectorAll('[data-step]');
    const progressDots = document.querySelectorAll('.mirror-modal__progress-dot');
    const progressLabel = document.querySelector('.mirror-modal__progress-label');
    const prevBtn = document.querySelector('[data-nav="prev"]');
    const nextBtn = document.querySelector('[data-nav="next"]');
    const submitBtn = document.querySelector('[data-submit-btn]');
    const submitBtnCollapsed = document.querySelector('[data-submit-btn-collapsed]');
    const modalLayout = document.querySelector('.mirror-modal__layout');

    // Hide all steps
    steps.forEach(step => {
      step.style.display = 'none';
    });

    // Show current step
    const currentStepEl = document.querySelector(`[data-step="${state.currentStep}"]`);
    if (currentStepEl) {
      currentStepEl.style.display = 'block';
    }

    // Progress label remains static (controlled by settings)
    // No need to update it dynamically

    // Update progress dots - only current step is active (blue and elongated)
    progressDots.forEach((dot, index) => {
      if (index + 1 === state.currentStep) {
        dot.classList.add('mirror-modal__progress-dot--active');
      } else {
        dot.classList.remove('mirror-modal__progress-dot--active');
      }
    });

    // Progressive disclosure of order summary sections
    const projectServiceSection = document.querySelector('[data-summary-section="project-service"]');
    const wallsSection = document.querySelector('[data-summary-section="walls"]');
    const pricingSection = document.querySelector('[data-summary-section="pricing"]');

    if (projectServiceSection) {
      // Show project/service info after completing step 1
      if (state.currentStep >= 1) {
        projectServiceSection.classList.add('mirror-modal__summary-section--visible');
      }
    }

    if (wallsSection) {
      // Show walls info after moving to step 2 or later
      if (state.currentStep >= 2) {
        wallsSection.classList.add('mirror-modal__summary-section--visible');
      }
    }

    if (pricingSection) {
      // Show pricing breakdown on step 3
      if (state.currentStep >= 3) {
        pricingSection.classList.add('mirror-modal__summary-section--visible');
      }
    }

    // Update navigation buttons
    if (prevBtn) {
      prevBtn.disabled = state.currentStep === 1;
    }

    // Toggle step-3 class on modal root for CSS targeting
    const modalRoot = document.querySelector('[data-mirror-modal]');
    if (modalRoot) {
      modalRoot.classList.toggle('mirror-modal--step-3', state.currentStep === 3);
    }

    if (nextBtn) {
      // On desktop step 3, keep enabled so it acts as Add to Cart
      nextBtn.disabled = false;
    }

    // Update submit button - only active on step 3
    if (submitBtn) {
      submitBtn.disabled = state.currentStep !== 3;
    }
    if (submitBtnCollapsed) {
      submitBtnCollapsed.disabled = state.currentStep !== 3;
    }

    updateConditionalFields();
    updateSummary();
    calculatePrice();

    // Scroll to top AFTER all DOM changes so the browser has settled the layout.
    // On mobile the sticky sidebar resizes when summary sections appear, which
    // cancels a smooth scroll that was started before the reflow.
    if (modalLayout) {
      requestAnimationFrame(() => {
        modalLayout.scrollTo({ top: 0, behavior: 'smooth' });
      });
    }
  }

  function updateConditionalFields() {
    const installationOnlyFields = document.querySelectorAll('[data-conditional="installation-only"]');
    
    installationOnlyFields.forEach(field => {
      if (state.serviceType === 'delivery-installation') {
        field.style.display = 'flex';
      } else {
        field.style.display = 'none';
      }
    });
  }

  function updateSummary() {
    const projectEl = document.querySelector('[data-summary="project"]');
    const serviceEl = document.querySelector('[data-summary="service"]');

    if (projectEl) {
      projectEl.textContent = state.projectType === 'residential' ? 'Residential' : 'Commercial';
    }

    if (serviceEl) {
      const serviceText = state.serviceType === 'delivery-installation' 
        ? 'Delivery + Installation' 
        : 'Delivery Only';
      serviceEl.textContent = serviceText;
    }

    updateSummaryDetails();
  }

  function updateSummaryDetails() {
    // Update walls summary
    const wallsContainer = document.querySelector('[data-summary-walls]');
    if (wallsContainer) {
      wallsContainer.innerHTML = '';
      
      // Calculate totals
      const totalWalls = state.walls.length;
      let qty36x60 = 0;
      let qty48x72 = 0;
      const finishes = {};
      
      state.walls.forEach(wall => {
        // Aggregate quantities by size
        if (wall.size === '36x60') {
          qty36x60 += wall.quantity;
        } else if (wall.size === '48x72') {
          qty48x72 += wall.quantity;
        }
        
        // Count finishes
        finishes[wall.finish] = (finishes[wall.finish] || 0) + 1;
      });
      
      // Determine which finish to display
      const finishEntries = Object.entries(finishes);
      const mostCommonFinish = finishEntries.reduce((a, b) => a[1] > b[1] ? a : b)[0];
      
      // Walls count
      const wallsRow = document.createElement('div');
      wallsRow.className = 'mirror-modal__summary-row';
      wallsRow.innerHTML = `
        <span class="mirror-modal__summary-label">Walls</span>
        <span class="mirror-modal__summary-value">${totalWalls}</span>
      `;
      wallsContainer.appendChild(wallsRow);
      
      // 36"x60" mirror quantity (only if > 0)
      if (qty36x60 > 0) {
        const size36Row = document.createElement('div');
        size36Row.className = 'mirror-modal__summary-row';
        size36Row.innerHTML = `
          <span class="mirror-modal__summary-label">36"x60" mirror</span>
          <span class="mirror-modal__summary-value">${qty36x60}</span>
        `;
        wallsContainer.appendChild(size36Row);
      }
      
      // 48"x72" mirror quantity (only if > 0)
      if (qty48x72 > 0) {
        const size48Row = document.createElement('div');
        size48Row.className = 'mirror-modal__summary-row';
        size48Row.innerHTML = `
          <span class="mirror-modal__summary-label">48"x72" mirror</span>
          <span class="mirror-modal__summary-value">${qty48x72}</span>
        `;
        wallsContainer.appendChild(size48Row);
      }
      
      // Channel finish
      const finishRow = document.createElement('div');
      finishRow.className = 'mirror-modal__summary-row';
      finishRow.innerHTML = `
        <span class="mirror-modal__summary-label">Channel finish</span>
        <span class="mirror-modal__summary-value">${getFinishLabel(mostCommonFinish)}</span>
      `;
      wallsContainer.appendChild(finishRow);
    }

    // Update pricing breakdown
    updatePricingBreakdown();

    // Update add-ons summary
    const addonsContainer = document.querySelector('[data-summary-addons]');
    if (addonsContainer) {
      addonsContainer.innerHTML = '';
      let hasAddons = false;
      
      // Installation Finish (only after user selects)
      if (state.serviceType === 'delivery-installation' && state.addons.installationFinish) {
        const row = document.createElement('div');
        row.className = 'mirror-modal__summary-row';
        row.innerHTML = `
          <span class="mirror-modal__summary-label">Installation Finish</span>
          <span class="mirror-modal__summary-value">${state.addons.installationFinish === 'premium' ? 'Premium' : 'Standard'}</span>
        `;
        addonsContainer.appendChild(row);
        hasAddons = true;
      }
      
      // Adhesive (optional)
      if (state.addons.adhesive) {
        const row = document.createElement('div');
        row.className = 'mirror-modal__summary-row';
        row.innerHTML = `
          <span class="mirror-modal__summary-label">Adhesive</span>
          <span class="mirror-modal__summary-value">Yes</span>
        `;
        addonsContainer.appendChild(row);
        hasAddons = true;
      }
      
      // Cutouts (optional)
      if (state.addons.cutouts && state.addons.cutoutsCount > 0) {
        // Map outlet cover type value to readable label
        const outletCoverLabels = {
          'light-switch': 'Light Switch Cover',
          'outlet': 'Outlet Cover',
          'both': 'Both (Light Switch + Outlet)'
        };
        const outletCoverLabel = outletCoverLabels[state.addons.outletCoverType] || state.addons.outletCoverType;
        
        const row = document.createElement('div');
        row.className = 'mirror-modal__summary-row';
        row.innerHTML = `
          <span class="mirror-modal__summary-label">Cutouts</span>
          <span class="mirror-modal__summary-value">${state.addons.cutoutsCount} × ${outletCoverLabel}</span>
        `;
        addonsContainer.appendChild(row);
        hasAddons = true;
      }
      
      // Wall Surface
      if (state.serviceType === 'delivery-installation' && state.addons.wallSurface) {
        const surfaceLabels = {
          'painted-drywall': 'Painted Drywall',
          'unpainted-drywall': 'Unpainted Drywall',
          'smooth-concrete': 'Smooth Concrete',
          'concrete-block': 'Concrete Block',
          'brick': 'Brick'
        };
        const row = document.createElement('div');
        row.className = 'mirror-modal__summary-row';
        row.innerHTML = `
          <span class="mirror-modal__summary-label">Wall Surface</span>
          <span class="mirror-modal__summary-value">${surfaceLabels[state.addons.wallSurface] || state.addons.wallSurface}</span>
        `;
        addonsContainer.appendChild(row);
        hasAddons = true;
      }
      
      // Show/hide add-ons section
      addonsContainer.style.display = hasAddons ? 'block' : 'none';
    }
  }

  function calculatePrice() {
    let total = 0;
    
    if (state.currentStep < 2) {
      state.totalPrice = 0;
      updateEstimate();
      return;
    }

    // Get product data
    const productData = window.mirrorConfigData || {};
    const mirrorProduct = productData.mirror;
    const addons = productData.addons || {};

    if (!window.mirrorConfigData) {
      return;
    }

    // Calculate mirror prices for all walls
    state.walls.forEach((wall) => {
      const mirrorPrice = getMirrorVariantPrice(mirrorProduct, wall.size, wall.finish);
      const wallTotal = mirrorPrice * wall.quantity;
      total += wallTotal;
    });

    // Calculate total mirrors (for add-ons that charge per mirror)
    const totalMirrors = state.walls.reduce((sum, wall) => sum + wall.quantity, 0);

    // Add-on: Installation ($50 per mirror)
    if (state.serviceType === 'delivery-installation' && addons.installation) {
      const installationPrice = getAddonPrice(addons.installation);
      const installationTotal = installationPrice * totalMirrors;
      total += installationTotal;
    }

    // Add-on: Premium Finish ($25 per mirror, only if installation selected and premium chosen)
    if (state.serviceType === 'delivery-installation' && state.addons.installationFinish === 'premium' && addons.premiumFinish) {
      const premiumFinishPrice = getAddonPrice(addons.premiumFinish);
      const premiumFinishTotal = premiumFinishPrice * totalMirrors;
      total += premiumFinishTotal;
    }

    // Add-on: Adhesive ($25 flat)
    if (state.addons.adhesive && addons.adhesive) {
      const adhesivePrice = getAddonPrice(addons.adhesive);
      total += adhesivePrice;
    }

    // Add-on: Cutouts ($150 per cutout)
    if (state.addons.cutouts && state.addons.cutoutsCount > 0 && addons.cutout) {
      const cutoutPrice = getAddonPrice(addons.cutout);
      const cutoutTotal = cutoutPrice * state.addons.cutoutsCount;
      total += cutoutTotal;
    }

    // Add-on: Wall Surface Surcharge ($50 per mirror, only for concrete/brick with installation)
    if (state.serviceType === 'delivery-installation' && needsWallSurcharge(state.addons.wallSurface) && addons.wallSurcharge) {
      const surchargePrice = getAddonPrice(addons.wallSurcharge);
      const surchargeTotal = surchargePrice * totalMirrors;
      total += surchargeTotal;
    }

    state.totalPrice = total;
    updateEstimate();
  }

  function updatePricingBreakdown() {
    const pricingContainer = document.querySelector('[data-summary-pricing]');
    if (!pricingContainer) return;

    pricingContainer.innerHTML = '';

    const productData = window.mirrorConfigData || {};
    const mirrorProduct = productData.mirror;
    const addons = productData.addons || {};

    let mirrorsTotal = 0;
    let installationTotal = 0;
    let premiumFinishTotal = 0;
    let adhesiveTotal = 0;
    let cutoutsTotal = 0;
    let wallSurchargeTotal = 0;
    const totalMirrors = state.walls.reduce((sum, wall) => sum + wall.quantity, 0);

    // Calculate mirrors total (variant price already includes Matte Black surcharge)
    state.walls.forEach((wall) => {
      const mirrorPrice = getMirrorVariantPrice(mirrorProduct, wall.size, wall.finish);
      mirrorsTotal += mirrorPrice * wall.quantity;
    });

    // Calculate installation if applicable
    if (state.serviceType === 'delivery-installation' && addons.installation) {
      const installationPrice = getAddonPrice(addons.installation);
      installationTotal = installationPrice * totalMirrors;
    }

    // Calculate premium finish if selected
    if (state.serviceType === 'delivery-installation' && state.addons.installationFinish === 'premium' && addons.premiumFinish) {
      const premiumFinishPrice = getAddonPrice(addons.premiumFinish);
      premiumFinishTotal = premiumFinishPrice * totalMirrors;
    }

    // Calculate adhesive if selected
    if (state.addons.adhesive && addons.adhesive) {
      const adhesivePrice = getAddonPrice(addons.adhesive);
      adhesiveTotal = adhesivePrice;
    }

    // Calculate cutouts if selected
    if (state.addons.cutouts && state.addons.cutoutsCount > 0 && addons.cutout) {
      const cutoutPrice = getAddonPrice(addons.cutout);
      cutoutsTotal = cutoutPrice * state.addons.cutoutsCount;
    }

    // Calculate wall surface surcharge if applicable
    if (state.serviceType === 'delivery-installation' && needsWallSurcharge(state.addons.wallSurface) && addons.wallSurcharge) {
      const surchargePrice = getAddonPrice(addons.wallSurcharge);
      wallSurchargeTotal = surchargePrice * totalMirrors;
    }

    // Mirrors row
    const mirrorsRow = document.createElement('div');
    mirrorsRow.className = 'mirror-modal__summary-row';
    mirrorsRow.innerHTML = `
      <span class="mirror-modal__summary-label">Mirrors</span>
      <span class="mirror-modal__summary-value">$${mirrorsTotal.toFixed(2)}</span>
    `;
    pricingContainer.appendChild(mirrorsRow);

    // Installation row (only if installation selected)
    if (installationTotal > 0) {
      const installationRow = document.createElement('div');
      installationRow.className = 'mirror-modal__summary-row';
      installationRow.innerHTML = `
        <span class="mirror-modal__summary-label">Installation (${totalMirrors} mirror${totalMirrors > 1 ? 's' : ''})</span>
        <span class="mirror-modal__summary-value">$${installationTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(installationRow);
    }

    // Premium finish row (only if premium selected)
    if (premiumFinishTotal > 0) {
      const premiumRow = document.createElement('div');
      premiumRow.className = 'mirror-modal__summary-row';
      premiumRow.innerHTML = `
        <span class="mirror-modal__summary-label">Premium finish (${totalMirrors} mirror${totalMirrors > 1 ? 's' : ''})</span>
        <span class="mirror-modal__summary-value">$${premiumFinishTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(premiumRow);
    }

    // Adhesive row (only if adhesive selected)
    if (adhesiveTotal > 0) {
      const adhesiveRow = document.createElement('div');
      adhesiveRow.className = 'mirror-modal__summary-row';
      adhesiveRow.innerHTML = `
        <span class="mirror-modal__summary-label">Adhesive</span>
        <span class="mirror-modal__summary-value">$${adhesiveTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(adhesiveRow);
    }

    // Cutouts row (only if cutouts selected)
    if (cutoutsTotal > 0) {
      // Map outlet cover type value to readable label
      const outletCoverLabels = {
        'light-switch': 'Light Switch',
        'outlet': 'Outlet',
        'both': 'Light Switch + Outlet'
      };
      const outletCoverLabel = outletCoverLabels[state.addons.outletCoverType] || state.addons.outletCoverType;
      
      const cutoutsRow = document.createElement('div');
      cutoutsRow.className = 'mirror-modal__summary-row';
      cutoutsRow.innerHTML = `
        <span class="mirror-modal__summary-label">Cutouts (${state.addons.cutoutsCount} × ${outletCoverLabel})</span>
        <span class="mirror-modal__summary-value">$${cutoutsTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(cutoutsRow);
    }

    // Wall surface row (only if surcharge applicable)
    if (wallSurchargeTotal > 0) {
      const wallSurfaceLabel = getWallSurfaceLabel(state.addons.wallSurface);
      const wallSurchargeRow = document.createElement('div');
      wallSurchargeRow.className = 'mirror-modal__summary-row';
      wallSurchargeRow.innerHTML = `
        <span class="mirror-modal__summary-label">${wallSurfaceLabel} (${totalMirrors} mirror${totalMirrors > 1 ? 's' : ''})</span>
        <span class="mirror-modal__summary-value">$${wallSurchargeTotal.toFixed(2)}</span>
      `;
      pricingContainer.appendChild(wallSurchargeRow);
    }
  }

  function getWallSurfaceLabel(wallSurface) {
    const labels = {
      'painted-drywall': 'Painted drywall',
      'unpainted-drywall': 'Unpainted drywall',
      'smooth-concrete': 'Smooth concrete',
      'concrete-block': 'Concrete block',
      'brick': 'Brick'
    };
    return labels[wallSurface] || wallSurface;
  }

  function parseShopifyVariantPrice(variant) {
    if (!variant || variant.price == null || variant.price === '') {
      return 0;
    }
    const raw = parseFloat(String(variant.price), 10);
    if (Number.isNaN(raw)) {
      return 0;
    }
    const str = String(variant.price);
    const hasDecimal = str.includes('.');
    if (!hasDecimal && raw >= 100) {
      return raw / 100;
    }
    return raw;
  }

  function getDomMirrorFallbackPrice(size) {
    const el = document.querySelector(`[data-price-${size}]`);
    if (!el) {
      return null;
    }
    const num = parseFloat(String(el.textContent).replace(/[^0-9.]/g, ''), 10);
    if (Number.isNaN(num) || num <= 0) {
      return null;
    }
    return num;
  }

  function getMirrorVariantPrice(mirrorProduct, size, finish) {
    const variants = getMirrorVariantsSource(mirrorProduct);
    if (variants.length) {
      const variant = findMirrorVariant(mirrorProduct, size, finish);
      if (variant) {
        const p = parseShopifyVariantPrice(variant);
        if (p > 0) {
          return p;
        }
      }
    }

    const dom = getDomMirrorFallbackPrice(size);
    if (dom != null && dom > 0) {
      return dom;
    }

    return 0;
  }

  function getAddonPrice(addonProduct) {
    if (!addonProduct) {
      return 0;
    }
    
    // Try to get price from first variant (if add-on has variants)
    if (addonProduct.variants && addonProduct.variants.length > 0) {
      const price = parseFloat(addonProduct.variants[0].price) / 100;
      return price;
    }
    
    // Try to get direct price property
    if (addonProduct.price) {
      const price = parseFloat(addonProduct.price) / 100;
      return price;
    }
    
    return 0;
  }

  function needsWallSurcharge(wallSurface) {
    const surchargeTypes = ['smooth-concrete', 'concrete-block', 'brick'];
    return surchargeTypes.includes(wallSurface);
  }

  function updateEstimate() {
    const totalPriceEl = document.querySelector('[data-total-price]');
    const totalPriceMobileEl = document.querySelector('[data-total-price-mobile]');
    const priceFormatted = `$${state.totalPrice.toFixed(2)}`;
    
    if (totalPriceEl) {
      totalPriceEl.textContent = priceFormatted;
    }
    
    if (totalPriceMobileEl) {
      totalPriceMobileEl.textContent = priceFormatted;
    }
  }

  /**
   * Each wall must use its own radio `name` group; duplicate names break multi-wall selection.
   */
  function applyWallRadioGroupNames(wallSection, wallIndex) {
    wallSection.querySelectorAll('.mirror-modal__size-radio').forEach((radio) => {
      radio.name = `mirror_size_wall_${wallIndex}`;
    });
    wallSection.querySelectorAll('.mirror-modal__finish-radio').forEach((radio) => {
      radio.name = `channel_finish_wall_${wallIndex}`;
    });
  }

  function initWallManagement() {
    const addWallBtn = document.querySelector('[data-action="add-wall"]');
    
    if (addWallBtn) {
      addWallBtn.addEventListener('click', addNewWall);
    }

    document.querySelectorAll('[data-wall-section]').forEach((section) => {
      const idx = parseInt(section.getAttribute('data-wall-section'), 10);
      if (!Number.isNaN(idx)) {
        applyWallRadioGroupNames(section, idx);
      }
    });

    // Initialize remove buttons for existing walls
    initRemoveButtons();
    updateRemoveButtonsVisibility();
  }

  function addNewWall() {
    const wallsContainer = document.querySelector('[data-walls-container]');
    const firstWall = document.querySelector('[data-wall-section="0"]');
    
    if (!wallsContainer || !firstWall) return;

    // Create new wall data
    const newWallIndex = state.walls.length;
    const newWall = {
      id: newWallIndex + 1,
      size: '36x60',
      finish: 'satin-silver',
      orientation: 'vertical',
      quantity: 1
    };
    state.walls.push(newWall);

    // Clone the first wall section
    const newWallSection = firstWall.cloneNode(true);
    newWallSection.setAttribute('data-wall-section', newWallIndex);
    
    // Update wall title
    const wallTitle = newWallSection.querySelector('.mirror-modal__wall-title');
    if (wallTitle) {
      wallTitle.textContent = `Wall ${newWallIndex + 1}`;
    }

    // Reset to default values
    resetWallSectionValues(newWallSection);
    applyWallRadioGroupNames(newWallSection, newWallIndex);

    // Insert before the "Add another wall" button
    const addWallField = document.querySelector('[data-action="add-wall"]').closest('.mirror-modal__field');
    wallsContainer.insertBefore(newWallSection, addWallField);

    // Reinitialize event listeners for all walls
    initButtonOptions();
    initCardOptions();
    initQuantitySelectors();
    initRemoveButtons();
    updateRemoveButtonsVisibility();

    calculatePrice();
  }

  function resetWallSectionValues(wallSection) {
    // Reset size cards to default (36x60)
    wallSection.querySelectorAll('.mirror-modal__size-card').forEach((card, index) => {
      const radio = card.querySelector('.mirror-modal__size-radio');
      if (radio && radio.value === '36x60') {
        card.classList.add('mirror-modal__size-card--selected');
        radio.checked = true;
      } else {
        card.classList.remove('mirror-modal__size-card--selected');
        if (radio) radio.checked = false;
      }
    });

    // Reset finish cards to default (satin-silver)
    wallSection.querySelectorAll('.mirror-modal__finish-card').forEach((card, index) => {
      const radio = card.querySelector('.mirror-modal__finish-radio');
      if (radio && radio.value === 'satin-silver') {
        card.classList.add('mirror-modal__finish-card--selected');
        radio.checked = true;
      } else {
        card.classList.remove('mirror-modal__finish-card--selected');
        if (radio) radio.checked = false;
      }
    });

    // Reset orientation buttons to default (vertical as shown in Figma)
    wallSection.querySelectorAll('[data-field="orientation"]').forEach(btn => {
      if (btn.getAttribute('data-value') === 'vertical') {
        btn.classList.add('mirror-modal__orientation-btn--active');
      } else {
        btn.classList.remove('mirror-modal__orientation-btn--active');
      }
    });

    // Reset quantity to 1
    const qtyInput = wallSection.querySelector('[data-field="quantity"]');
    if (qtyInput) {
      qtyInput.value = 1;
    }
  }

  function initRemoveButtons() {
    const removeButtons = document.querySelectorAll('[data-remove-wall]');
    
    removeButtons.forEach(btn => {
      // Remove old listeners by cloning
      const newBtn = btn.cloneNode(true);
      btn.parentNode.replaceChild(newBtn, btn);
      
      newBtn.addEventListener('click', () => {
        const wallIndex = parseInt(newBtn.getAttribute('data-remove-wall'));
        removeWall(wallIndex);
      });
    });
  }

  function removeWall(wallIndex) {
    if (state.walls.length === 1) {
      alert('You must have at least one wall configuration.');
      return;
    }

    // Remove from state
    state.walls.splice(wallIndex, 1);

    // Remove from DOM
    const wallSection = document.querySelector(`[data-wall-section="${wallIndex}"]`);
    if (wallSection) {
      wallSection.remove();
    }

    // Reindex all remaining wall sections
    const allWallSections = document.querySelectorAll('[data-wall-section]');
    allWallSections.forEach((section, index) => {
      section.setAttribute('data-wall-section', index);
      applyWallRadioGroupNames(section, index);

      const wallTitle = section.querySelector('.mirror-modal__wall-title');
      if (wallTitle) {
        wallTitle.textContent = `Wall ${index + 1}`;
      }

      const removeBtn = section.querySelector('[data-remove-wall]');
      if (removeBtn) {
        removeBtn.setAttribute('data-remove-wall', index);
      }
    });

    updateRemoveButtonsVisibility();
    calculatePrice();
  }

  function updateRemoveButtonsVisibility() {
    const removeButtons = document.querySelectorAll('[data-remove-wall]');
    const showRemove = state.walls.length > 1;
    
    removeButtons.forEach(btn => {
      btn.style.display = showRemove ? 'flex' : 'none';
    });
  }

  function getSizeLabel(size) {
    return size === '36x60' ? '36" × 60"' : '48" × 72"';
  }

  function getFinishLabel(finish) {
    return finish === 'satin-silver' ? 'Satin Silver' : 'Matte Black';
  }

  function getOrientationLabel(orientation) {
    return orientation === 'horizontal' ? 'Horizontal' : 'Vertical';
  }

  function initAddonProducts() {
    if (!window.mirrorConfigData) {
      return;
    }

    const addonProducts = window.mirrorConfigData.addonProducts || {};

    // Build addons object from product objects passed from Liquid
    const addons = {};

    if (addonProducts.installation && addonProducts.installation.variants && addonProducts.installation.variants.length > 0) {
      const variant = addonProducts.installation.variants[0];
      addons.installation = {
        title: addonProducts.installation.title || 'Professional Installation',
        price: variant.price,
        variants: [variant]
      };
    }

    if (addonProducts.premiumFinish && addonProducts.premiumFinish.variants && addonProducts.premiumFinish.variants.length > 0) {
      const variant = addonProducts.premiumFinish.variants[0];
      addons.premiumFinish = {
        title: addonProducts.premiumFinish.title || 'Premium Installation Finish',
        price: variant.price,
        variants: [variant]
      };
    }

    if (addonProducts.adhesive && addonProducts.adhesive.variants && addonProducts.adhesive.variants.length > 0) {
      const variant = addonProducts.adhesive.variants[0];
      addons.adhesive = {
        title: addonProducts.adhesive.title || 'Adhesive',
        price: variant.price,
        variants: [variant]
      };
    }

    if (addonProducts.cutout && addonProducts.cutout.variants && addonProducts.cutout.variants.length > 0) {
      const variant = addonProducts.cutout.variants[0];
      addons.cutout = {
        title: addonProducts.cutout.title || 'Outlet/Switch Cutout + Cover',
        price: variant.price,
        variants: [variant]
      };
    }

    if (addonProducts.wallSurcharge && addonProducts.wallSurcharge.variants && addonProducts.wallSurcharge.variants.length > 0) {
      const variant = addonProducts.wallSurcharge.variants[0];
      addons.wallSurcharge = {
        title: addonProducts.wallSurcharge.title || 'Wall Surface Surcharge',
        price: variant.price,
        variants: [variant]
      };
    }

    window.mirrorConfigData.addons = addons;
  }

  function syncSizeCardPrices() {
    const productData = window.mirrorConfigData || {};
    const mirrorProduct = productData.mirror;
    if (!mirrorProduct) return;

    ['36x60', '48x72'].forEach(size => {
      const variant = findMirrorVariant(mirrorProduct, size, 'satin-silver');
      if (!variant) return;
      const price = parseShopifyVariantPrice(variant);
      if (price <= 0) return;
      document.querySelectorAll(`[data-price-${size}]`).forEach(el => {
        el.textContent = `$${price.toFixed(2)}`;
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    // Initialize addon products from hardcoded variant IDs
    initAddonProducts();
    
    // Initialize everything
    initModal();
    initAccordion();
    initOptions();
    initButtonOptions();
    initCardOptions();
    initQuantitySelectors();
    initCheckboxes();
    initSelects();
    initPhotoUpload();
    initNavigation();
    initWallManagement();
    syncSizeCardPrices();
    updateSummary();
    calculatePrice();
    updateConditionalFields();
    updateStep();
  });
})();
